import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/ToolTip";
import { useEffect, useMemo, useRef, useState } from "react";
import { Chess as SanChess } from "@chesshotel/chess";
import { Chess } from "@chesshotel/chesslib";
import type { Key } from "@/chessBoard/components/types";
import ResizableBoardFrame from "@/components/board/ResizableBoardFrame";
import Board from "@/chessBoard/components/Board";
import { ANIMATION_DURATION } from "@/chessBoard/components/animations";
import { useSettings } from "@/redux/settings";
import { convertMoves } from "@/util/moves";
import type { PracticePuzzle } from "./catalog";
import { FaCog, FaHeart, FaRegHeart, FaSignal } from "react-icons/fa";
import styles from "./PuzzleBoard.module.css";
import useTranslation from "next-translate/useTranslation";

export type PuzzleAction = {
  action: "move" | "hint" | "reveal";
  move?: string;
  requestId: string;
};
export type PuzzleReply = {
  fen: string;
  correct?: boolean;
  done?: boolean;
  failed?: boolean;
  highlight?: string;
  solution?: string;
  message?: string;
  hearts?: number;
  hintAvailable?: boolean;
  ratingSettled?: boolean;
};

export default function PracticeBoard({
  puzzle,
  onAction,
  onNext,
  nextLoading,
  nextLabel,
  explanation,
  initialReply,
  initialHearts = 3,
  initialHintAvailable = true,
  initialRatingSettled = false,
  onOpenDifficultySettings,
  children,
  layout = "practice",
}: {
  puzzle: Pick<PracticePuzzle, "id" | "fen"> & Partial<PracticePuzzle>;
  onAction?: (action: PuzzleAction) => Promise<PuzzleReply>;
  onNext?: () => void;
  nextLoading?: boolean;
  nextLabel?: string;
  explanation?: string;
  initialReply?: PuzzleReply;
  initialHearts?: number;
  initialHintAvailable?: boolean;
  initialRatingSettled?: boolean;
  onOpenDifficultySettings?: () => void;
  children?: React.ReactNode;
  layout?: "practice" | "league" | "mistakes";
}) {
  const { t } = useTranslation("puzzles");
  const resolvedNextLabel = nextLabel || t("general.nextPuzzle");
  const settings = useSettings();
  const league = layout !== "practice";
  const ratedLeague = layout === "league";
  const leagueBoardSize = settings.boardSize || 504;
  const container = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState(320);
  const [fen, setFen] = useState(initialReply?.fen || puzzle.fen);
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const alive = useRef(true);
  const pending = useRef<PuzzleAction | null>(null);
  const [done, setDone] = useState(!!initialReply?.done);
  const [revealed, setRevealed] = useState(!!initialReply?.failed);
  const [highlight, setHighlight] = useState<string | null>(
    initialReply?.highlight || null,
  );
  const [feedback, setFeedback] = useState(() =>
    initialReply?.failed
      ? t("general.localReveal")
      : initialReply?.done
        ? t("general.localSolved")
        : initialReply?.correct
          ? t("general.localCorrect")
          : "",
  );
  const [error, setError] = useState("");
  const [solution, setSolution] = useState(initialReply?.solution || "");
  const [hearts, setHearts] = useState(initialHearts);
  const [heartAnimationKey, setHeartAnimationKey] = useState(0);
  const [hintAvailable, setHintAvailable] = useState(initialHintAvailable);
  const [ratingSettled, setRatingSettled] = useState(initialRatingSettled);
  const [lastMoveIncorrect, setLastMoveIncorrect] = useState(false);
  const [typedMove, setTypedMove] = useState("");
  const [keyboardEntry, setKeyboardEntry] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const white = puzzle.fen.split(" ")[1] === "w";
  const moves = (puzzle.moves || "").split(/\s+/).filter(Boolean);
  const practiceDifficulty =
    !league && typeof puzzle.rating === "number" ? puzzle.rating : null;
  const practiceDifficultyLabel =
    practiceDifficulty === null
      ? ""
      : t("league.difficulty", {
          rating: practiceDifficulty.toLocaleString(),
        });

  useEffect(() => {
    alive.current = true;
    if (league)
      return () => {
        alive.current = false;
      };
    const observer = new ResizeObserver(([entry]) =>
      setSize(
        Math.max(
          160,
          Math.floor(
            Math.min(
              window.matchMedia("(min-width: 1024px)").matches ? 504 : Infinity,
              entry.contentRect.width -
                (window.matchMedia("(min-width: 768px)").matches ? 44 : 0),
            ) / 4,
          ) * 4,
        ),
      ),
    );
    if (container.current) observer.observe(container.current);
    return () => {
      alive.current = false;
      observer.disconnect();
    };
  }, [league]);

  const possibleMoves = useMemo(() => {
    if (busy || done || revealed || pending.current) return {};
    try {
      return convertMoves(Chess(fen).moves({ verbose: true }));
    } catch {
      return {};
    }
  }, [fen, busy, done, revealed, resetKey]);

  function localAction(action: PuzzleAction): PuzzleReply {
    const expected = moves[index];
    if (action.action === "hint")
      return {
        fen,
        highlight: expected?.slice(0, 2),
        message: t("general.localHint"),
      };
    if (action.action === "reveal") {
      const board = new SanChess(puzzle.fen);
      const line = moves
        .map(
          (move) =>
            board.move({
              from: move.slice(0, 2),
              to: move.slice(2, 4),
              promotion: move[4],
            })?.san,
        )
        .join(" ");
      return {
        fen,
        solution: line,
        failed: true,
        message: t("general.localReveal"),
      };
    }
    if (action.move !== expected)
      return {
        fen,
        correct: false,
        message: t("general.localWrong"),
      };
    const board = new SanChess(fen);
    board.move({
      from: expected.slice(0, 2),
      to: expected.slice(2, 4),
      promotion: expected[4],
    });
    const opponent = moves[index + 1];
    if (opponent)
      board.move({
        from: opponent.slice(0, 2),
        to: opponent.slice(2, 4),
        promotion: opponent[4],
      });
    const finished = index + 2 >= moves.length;
    setIndex(index + 2);
    return {
      fen: board.fen(),
      correct: true,
      done: finished,
      message: finished ? t("general.localSolved") : t("general.localCorrect"),
    };
  }

  async function act(action: PuzzleAction) {
    if (busyRef.current || done || revealed) return;
    busyRef.current = true;
    setBusy(true);
    setError("");
    pending.current = action;
    try {
      const reply = onAction ? await onAction(action) : localAction(action);
      if (!alive.current) return;
      // Keep the mounted board and show the player's position before the reply.
      // Remounting every turn discards the board's previous piece positions.
      if (action.action === "move" && action.move && reply.correct) {
        const board = new SanChess(fen);
        const move = action.move;
        const played = board.move({
          from: move.slice(0, 2),
          to: move.slice(2, 4),
          promotion: move[4],
        });
        if (played && board.fen() !== reply.fen) {
          setFen(board.fen());
          setFeedback(t("general.opponentMoving"));
          await new Promise((resolve) =>
            setTimeout(resolve, ANIMATION_DURATION),
          );
          if (!alive.current) return;
        }
      }
      pending.current = null;
      setFen(reply.fen);
      setDone(!!reply.done);
      setRevealed(!!reply.failed);
      setHighlight(reply.highlight || null);
      setSolution(reply.solution || "");
      setFeedback(
        action.action === "hint"
          ? t("general.localHint")
          : action.action === "reveal"
            ? t("general.localReveal")
            : reply.done
              ? t("general.localSolved")
              : reply.correct
                ? t("general.localCorrect")
                : t("general.localWrong"),
      );
      if (ratedLeague) {
        if (reply.hearts !== undefined && reply.hearts < hearts) {
          setHeartAnimationKey((key) => key + 1);
        }
        if (reply.hearts !== undefined) setHearts(reply.hearts);
        if (reply.hintAvailable !== undefined)
          setHintAvailable(reply.hintAvailable);
        if (reply.ratingSettled !== undefined)
          setRatingSettled(reply.ratingSettled);
        if (action.action === "move" && reply.correct === false) {
          setLastMoveIncorrect(true);
        } else if (action.action === "move") {
          setLastMoveIncorrect(false);
        } else if (action.action === "hint") {
          setLastMoveIncorrect(false);
        }
      }
      setTypedMove("");
      if (action.action === "move" && !reply.correct) {
        setResetKey((value) => value + 1);
      } else if (action.action === "move" && reply.correct) {
        // Prevent another move until the opponent's animation finishes.
        await new Promise((resolve) => setTimeout(resolve, ANIMATION_DURATION));
      }
    } catch {
      if (alive.current) setError(t("general.saveError"));
    } finally {
      busyRef.current = false;
      if (alive.current) setBusy(false);
    }
  }

  function send(action: PuzzleAction["action"], move?: string) {
    const requestId = Array.from(
      crypto.getRandomValues(new Uint8Array(16)),
      (byte) => byte.toString(16).padStart(2, "0"),
    ).join("");
    void act({ action, move, requestId });
  }

  function submitText(event: React.FormEvent) {
    event.preventDefault();
    try {
      const board = new SanChess(fen);
      const input = typedMove.trim();
      const move = /^[a-h][1-8][a-h][1-8][qrbn]?$/i.test(input)
        ? board.move({
            from: input.slice(0, 2).toLowerCase(),
            to: input.slice(2, 4).toLowerCase(),
            promotion: input[4]?.toLowerCase(),
          })
        : board.move(input);
      if (move) send("move", move.from + move.to + (move.promotion || ""));
    } catch {
      setFeedback(t("general.legalMoveError"));
    }
  }

  function retryPuzzle() {
    if (busyRef.current || nextLoading || onAction) return;
    setFen(puzzle.fen);
    setIndex(0);
    setDone(false);
    setRevealed(false);
    setHighlight(null);
    setFeedback("");
    setSolution("");
    setHearts(initialHearts);
    setHeartAnimationKey(0);
    setHintAvailable(initialHintAvailable);
    setRatingSettled(initialRatingSettled);
    setLastMoveIncorrect(false);
    setError("");
    setTypedMove("");
    pending.current = null;
    setResetKey((value) => value + 1);
  }

  const stopped = busy || done || revealed || !!error || !!nextLoading;
  return (
    <div
      className={
        league
          ? "mx-auto flex w-full flex-col items-center md:mt-4 lg:w-fit lg:flex-row lg:items-start lg:gap-4"
          : "grid items-start gap-6 lg:grid-cols-[minmax(0,548px)_minmax(240px,1fr)]"
      }
    >
      <div
        ref={container}
        className="min-w-0"
        aria-label={t("general.toMoveBoard", {
          color: t(white ? "general.white" : "general.black"),
        })}
      >
        <ResizableBoardFrame
          className="w-fit max-w-full"
          size={league ? leagueBoardSize : size}
          enabled={league && settings.isDesktop !== false}
        >
          <Board
            key={`${puzzle.id}-${resetKey}`}
            fen={fen}
            size={league ? leagueBoardSize : size}
            playingWhite={white}
            settings={settings}
            possibleMoves={stopped ? {} : possibleMoves}
            highLightKey={highlight as Key | null}
            onMoveMade={(move) =>
              send("move", move.from + move.to + (move.promotion || ""))
            }
          />
        </ResizableBoardFrame>
      </div>
      <div
        className={
          league
            ? "flex w-full flex-col rounded-md bg-athens-gray-150 text-sm lg:w-[400px]"
            : "rounded-md bg-chess-ui-panel p-5"
        }
        style={
          league && settings.isDesktop
            ? { minHeight: leagueBoardSize + 44 }
            : undefined
        }
      >
        {league && (
          <div className="rounded-t-md bg-chess-ui-header px-5 py-3 font-bold text-white">
            <h1>
              {t(layout === "mistakes" ? "nav.mistakes" : "league.title")}
            </h1>
          </div>
        )}
        <div className={league ? "flex flex-1 flex-col p-3 md:p-4" : undefined}>
          <div
            className={
              league ? "relative rounded-md bg-white p-4 shadow-sm" : "relative"
            }
          >
            <div
              className={
                ratedLeague || practiceDifficulty !== null ? "pr-20" : undefined
              }
            >
              <p className="text-sm font-semibold uppercase tracking-wide text-altblue-700">
                {done
                  ? t("general.solved")
                  : revealed
                    ? t("general.solution")
                    : lastMoveIncorrect
                      ? t("general.notQuite")
                      : t(
                          white ? "general.whiteToMove" : "general.blackToMove",
                        )}
              </p>
              <h2
                className="mt-2 text-xl font-semibold text-slate-900"
                aria-live={done && !league ? "polite" : undefined}
              >
                {done
                  ? t(!league ? "league.readyNext" : "general.youFoundIt")
                  : revealed
                    ? t("general.learnIdea")
                    : lastMoveIncorrect
                      ? t("general.keepTrying")
                      : t("general.findBestMove")}
              </h2>
              {(!done || league) && (busy || feedback) && (
                <p
                  className="mt-2 text-base leading-6 text-slate-600"
                  role="status"
                  aria-live="polite"
                >
                  {busy ? t("general.checkingMove") : feedback}
                </p>
              )}
            </div>
            {practiceDifficulty !== null && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    tabIndex={0}
                    aria-label={practiceDifficultyLabel}
                    className="absolute right-0 top-0 inline-flex cursor-help items-center gap-1.5 whitespace-nowrap rounded-full bg-athens-gray-100 px-2.5 py-1.5 text-xs font-semibold leading-none text-slate-700 outline-none transition-colors hover:bg-athens-gray-200 focus-visible:ring-2 focus-visible:ring-altblue-300"
                  >
                    <FaSignal className="text-slate-500" aria-hidden="true" />
                    <span aria-hidden="true">
                      {practiceDifficulty.toLocaleString()}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" sideOffset={6}>
                  <p>{practiceDifficultyLabel}</p>
                </TooltipContent>
              </Tooltip>
            )}
            {ratedLeague && (
              <div
                role="status"
                aria-label={t("league.ratingLives", { count: hearts })}
                className="absolute right-4 top-4 flex items-center gap-1 text-xs"
              >
                {[0, 1, 2].map((heart) => {
                  const filled = heart < hearts;
                  const justLost = heartAnimationKey > 0 && heart === hearts;
                  return (
                    <span
                      key={heart}
                      className="relative inline-flex h-3.5 w-3.5 items-center justify-center"
                    >
                      {filled ? (
                        <FaHeart
                          className="text-altblue-500"
                          aria-hidden="true"
                        />
                      ) : (
                        <FaRegHeart
                          className="text-gray-300"
                          aria-hidden="true"
                        />
                      )}
                      {justLost && (
                        <FaHeart
                          key={`${heartAnimationKey}-${heart}`}
                          className={`${styles.heartLost} pointer-events-none absolute text-altblue-500`}
                          aria-hidden="true"
                        />
                      )}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
          {ratedLeague &&
            ratingSettled &&
            hearts === 0 &&
            !lastMoveIncorrect &&
            !revealed &&
            !done && (
              <div className="mt-3 rounded-md bg-white/55 px-3 py-2 text-sm text-slate-600">
                <p className="font-semibold text-slate-800">
                  {t("general.practiceMode")}
                </p>
                <p className="mt-0.5 text-xs">
                  {t("general.ratingRecordedKeepSolving")}
                </p>
              </div>
            )}
          {league && !done && !revealed && (
            <Button
              variant="link"
              size="default"
              type="button"
              className="mt-3"
              onClick={() => setKeyboardEntry(!keyboardEntry)}
              aria-expanded={keyboardEntry}
            >
              {t("general.enterKeyboardMove")}
            </Button>
          )}
          {!done && !revealed && league && keyboardEntry && (
            <form onSubmit={submitText} className="mt-5">
              <label
                htmlFor={`move-${puzzle.id}`}
                className="mb-1 block text-sm font-medium text-slate-600"
              >
                {t("general.yourMove")}
              </label>
              <div className="flex gap-2">
                <input
                  id={`move-${puzzle.id}`}
                  value={typedMove}
                  onChange={(event) => setTypedMove(event.target.value)}
                  autoComplete="off"
                  placeholder={t("general.movePlaceholder")}
                  disabled={stopped}
                  className="min-w-0 flex-1 rounded border border-slate-300 px-3 py-2 text-base text-slate-900"
                />
                <Button
                  variant="default"
                  size="default"
                  type="submit"
                  disabled={stopped || !typedMove.trim()}
                >
                  {t("general.play")}
                </Button>
              </div>
            </form>
          )}
          {error && (
            <div role="alert" className="mt-4 text-sm text-red-700">
              <p>{error}</p>
              <Button
                variant="neutral"
                size="default"
                type="button"
                className="mt-2"
                disabled={busy}
                onClick={() => pending.current && void act(pending.current)}
              >
                {t("general.retrySaving")}
              </Button>
            </div>
          )}
          <div className="mt-5 flex flex-wrap gap-3">
            {!done && !revealed && (!ratedLeague || hearts > 0) && (
              <>
                <Button
                  variant="subtle"
                  size="default"
                  type="button"
                  onClick={() => send("hint")}
                  disabled={
                    stopped || (ratedLeague && (!hintAvailable || hearts === 0))
                  }
                >
                  {t("general.hint")}
                </Button>
                {(!ratedLeague || hearts === 0) && (
                  <Button
                    variant="quiet"
                    size="default"
                    type="button"
                    onClick={() => send("reveal")}
                    disabled={stopped}
                  >
                    {t("general.showSolution")}
                  </Button>
                )}
              </>
            )}
            {revealed && !onAction && (
              <Button
                variant="neutral"
                size="default"
                type="button"
                onClick={retryPuzzle}
                disabled={busy || nextLoading}
              >
                {t("general.tryAgain")}
              </Button>
            )}
            {onNext &&
              (done ||
                revealed ||
                (ratedLeague && ratingSettled && hearts === 0)) && (
                <Button
                  variant="default"
                  size="default"
                  type="button"
                  disabled={nextLoading || busy}
                  onClick={onNext}
                >
                  {nextLoading ? t("general.loading") : resolvedNextLabel}
                </Button>
              )}
            {onOpenDifficultySettings && (
              <Button
                variant="quiet"
                size="icon"
                type="button"
                className="ml-auto"
                onClick={onOpenDifficultySettings}
                aria-label={t("league.settings")}
              >
                <FaCog aria-hidden="true" />
              </Button>
            )}
          </div>
          {solution && (
            <p className="mt-4 rounded bg-athens-gray-100 p-3 font-mono text-sm leading-6 text-slate-700">
              {solution}
            </p>
          )}
          {(done || revealed) && explanation && (
            <p className="mt-4 text-base leading-6 text-slate-600">
              {explanation}
            </p>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}
